import { useEffect, useRef, useState } from 'react';
import type { HandoffIntent } from '@chat-emp/shared';

import { stageHintFor } from '../data/inbox.js';
import { useConversation, useConversationDetails } from '../data/source.js';
import { useForms } from '../data/forms.js';
import { useSession } from '../session/SessionProvider.js';
import type { Attachment, ClosingVariant, InboxItem, Message } from '../fixtures/domain.js';
import {
  applicationStatusOf,
  closingVariantFor,
  isBroadcast,
  isJobBounded,
  type HiringStage,
} from '../fixtures/rules.js';
import { firstReplyReward } from '../fixtures/rules.js';
import { type ComposerHandle } from '../conversation/Composer.js';
import { ConversationComposer } from '../conversation/ConversationComposer.js';
import { ConversationHeader } from '../conversation/ConversationHeader.js';
import { DeletionCountdown } from '../conversation/DeletionCountdown.js';
import { HiringStatusPill } from '../conversation/HiringStatusPill.js';
import { StageBar } from '../conversation/StageBar.js';
import { EndChatDialog } from '../conversation/EndChatDialog.js';
import { trackHiringStage, type HiringSurface } from '../conversation/hiringEvents.js';
import { AssistantCard, isRecommendedDraft } from '../conversation/AssistantCard.js';
import type { CardAction } from '../conversation/assistantActions.js';
import {
  trackAssistantShown,
  trackAssistantTaken,
  trackAssistantToggled,
  trackingTranslation,
} from '../conversation/assistantEvents.js';
import { footerCard } from '../conversation/footerCard.js';
import { useAssistant } from '../hooks/useAssistant.js';
import { useQuickReplies } from '../hooks/useQuickReplies.js';
import { MessageThread } from '../conversation/MessageThread.js';
import { ReportDialog } from '../conversation/ReportDialog.js';
import { TemplatePanel } from '../conversation/TemplatePanel.js';
import { useComposerDraft } from '../conversation/useComposerDraft.js';
import { useTemplates } from '../conversation/useTemplates.js';
import { Icon } from '../components/Icon.js';
import { cidParts, emitToHost } from '../lib/bridge.js';
import { track } from '../lib/analytics.js';
import headerStyles from './WidgetHeader.module.css';
import styles from './WidgetThread.module.css';

interface WidgetThreadProps {
  item: InboxItem;
  onSent: () => void;
  /**
   * Something modal is over this thread. The panel around us dismisses on
   * Escape, which would otherwise shut the whole widget out from under an open
   * dialog — taking the dialog, the thread and a half-written reply with it.
   */
  onModal?: (open: boolean) => void;
  /** Absent in pane mode, where the host opened this one chat and there is no list behind it. */
  onBack?: () => void;
  onClose: () => void;
  /** Pane mode: the header identity asks the host for its profile instead of /chat's details. */
  onViewProfile?: () => void;
  /** Single-chat pane: its events report `surface: 'pane'`, not the floating widget's. */
  pane?: boolean;
  /**
   * Hand this chat to /chat, naming what to open on arrival where there is
   * something to name. The group's rows always name one; the header's fallback
   * expand, drawn only when there is no group, asks for the chat and no more.
   */
  onExpand: (intent?: HandoffIntent) => void;
}

/**
 * One conversation: reply to it, and move it along the hiring pipeline.
 *
 * The split against /chat's conversation is no longer all-or-nothing. A stage
 * move is one tap with a visible result, nothing leaves in the employer's name,
 * and it settles nothing — a stage can always be moved again — so Shortlist /
 * Interviewed / Hired belong right here, beside the reply that earned them.
 *
 * Ending happens here too, as the mock has it: End chat / End & reject open the
 * same closing-message dialog /chat does, and nothing ends until the employer
 * confirms there. Only Details hands the chat over (`?open=details`): it wants a
 * reference column this box hasn't got.
 *
 * The header and stage bar are /chat's own, in their narrow form, as the mock draws
 * them. Still omitted outright: quick replies.
 *
 * Templates are NOT one of those omissions. The composer is the shared one, so
 * the + menu offers Template here as it does on the page; wiring it to nothing
 * left a menu item that ate the click. It writes into the box the employer then
 * reads and edits — the opposite of an irreversible tap.
 */
export function WidgetThread({
  item,
  onSent,
  onModal,
  onBack,
  onClose,
  onViewProfile,
  onExpand,
  pane,
}: WidgetThreadProps) {
  const surface = pane ? 'pane' : 'widget';
  const { channel, counterpart } = item;
  const convo = useConversation(channel.id);
  const formLibrary = useForms();
  const { messages, loading, sending, ended, send, uploadAttachment, isOwnMessage, currentUserId } =
    convo;
  const composerRef = useRef<ComposerHandle>(null);
  const understood = () => {
    convo.dismissBounce();
    composerRef.current?.focus();
  };
  const { gamification, sessionReady } = useSession();
  const reward = firstReplyReward(channel, gamification);
  const draft = useComposerDraft(channel.id, convo, counterpart.name, composerRef);
  const templates = useTemplates();
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [report, setReport] = useState<{ message?: Message } | null>(null);
  const [countdownOpen, setCountdownOpen] = useState(false);
  // A stage or ending write that did not land. The overlay rolls itself back, so
  // without this the bar just snaps back unexplained. Same copy as /chat.
  const [writeError, setWriteError] = useState<string | null>(null);
  /** The closing-message dialog, and which control opened it. */
  const [ending, setEnding] = useState<{ variant: ClosingVariant; surface: HiringSurface } | null>(
    null,
  );

  const jobBounded = isJobBounded(channel);
  // The hiring stage (ES) and the profile lock live off the Stream channel, so every
  // chat with a candidate reads them as /chat does. No Stream connection, no MAU.
  const details = useConversationDetails(isBroadcast(channel) ? null : channel.id);
  // The inbox page's stage stands in until `/details` lands, so the stage bar
  // draws on open; `/details` (ES) still wins once it answers.
  const hint = stageHintFor(channel.id);
  const status =
    convo.statusOverride ?? details.applicationStatus ?? hint ?? applicationStatusOf(channel);
  // No stage read off ES yet, so nothing may be pressed: the hint can be stale,
  // or the read model's seeded `pending`. An override means the employer just acted.
  const statusUnknown = jobBounded && details.loading && !convo.statusOverride;
  // Enough to draw the bar and tag: an ES answer, an override, or the hint.
  const stageDrawable = !statusUnknown || hint !== undefined;

  // A rejected applicant is terminal even before the channel closes server-side,
  // exactly as on /chat: the group would otherwise offer to re-shortlist them.
  const terminal = ended || status === 'rejected';

  /*
   * A conversation worth a reading, on /chat's own terms.
   *
   * `statusUnknown` is the one that earns its place here rather than there: this
   * panel mounts on every host page, and spending a reading on a chat that turns
   * out to be rejected is the waste that is entirely avoidable by waiting a few
   * hundred milliseconds for the ES status.
   *
   * No broadcast guard, for the reason `actions` has none — the widget lists
   * open chats only, and an announcement is never in that list.
   */
  const liveConversation = sessionReady && !terminal && !statusUnknown;
  const assistant = useAssistant(channel.id, messages, liveConversation);
  /*
   * THE READING IS SPENT ON OPENING THE THREAD, not on opening the card.
   *
   * Gating it on the expand looks like the cheaper option and guts the design:
   * the shut card's whole payload is the hint naming the move, and the hint is
   * drawn from a reading. Shut with nothing read is the arrow this card replaced.
   *
   * `liveConversation` is the lever if that trade is ever worth taking.
   */
  const card = footerCard(liveConversation, false, assistant.enabled, assistant.silent);

  /*
   * Static openers, and never the AI lane — so this costs no call at all.
   *
   * /chat runs the lane where the switch is OFF. Here that case draws no card:
   * `footerCard` is passed `awaitingReply: false`, so switch-off is 'none' and
   * AI-2704's promise holds. The only `legacy` the widget can reach is a reading
   * that came back empty, and /chat gives that one the static set too — a card
   * standing in for a dead request must not spend a second one just as likely
   * to die.
   */
  const quickReplies = useQuickReplies(channel.id, messages, isOwnMessage, false);
  /** Shut is the default: the card lands as one row and expanding asks for the move. */
  const [assistantOpen, setAssistantOpen] = useState(false);
  const assistantRef = useRef<HTMLDivElement>(null);

  // Leaving the thread with the panel open (back, or the widget closing) must
  // not leave the widget believing a dialog is still over it — Escape would
  // stay dead for the rest of the session.
  const onModalRef = useRef(onModal);
  onModalRef.current = onModal;
  useEffect(() => () => onModalRef.current?.(false), []);

  // Anything that can sit over this thread and both have to be reported, or Escape
  // closes the widget out from under whichever one didn't.
  useEffect(() => {
    onModalRef.current?.(templatesOpen || countdownOpen || ending !== null || report !== null);
  }, [templatesOpen, countdownOpen, ending, report]);

  const submit = async (text: string, attachments?: Attachment[]) => {
    const messageId = await draft.submit(text, attachments);
    onSent();
    return messageId;
  };

  /**
   * Its own in-flight flag, because the seam's `sending` is a constant `false`
   * on both branches — passing that as `busy` would have drawn a guard that was
   * never armed, and a double-tap on a stage would have issued two writes.
   *
   * No `onSent()`, unlike a send: the list behind the panel shows a last message,
   * an unread dot and a needs-reply mark, none of which a stage move touches. The
   * button above re-reads its own label from the store overlay, so re-fetching a
   * whole inbox page here would cost a BFF round trip to change nothing.
   */
  const [saving, setSaving] = useState(false);
  const changeStage = async (stage: HiringStage) => {
    setWriteError(null);
    setSaving(true);
    try {
      await convo.setApplicationStatus(stage);
    } catch {
      setWriteError('Could not update the hiring stage.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Taking a row the assistant offered.
   *
   * Reported before the act, so a move that fails still records that the
   * employer agreed with it — the order /chat uses, for the same reason.
   *
   * No `trackHiringStage` beside it: /chat's assistant card does not fire one
   * either, and a press counted once as a hiring event and once as an assistant
   * event is the same press in two funnels.
   */
  const takeAssistantAction = (action: CardAction, by: 'press' | 'shortcut' = 'press') => {
    assistant.select('action', action);
    trackAssistantTaken({
      action,
      recommended: assistant.suggest === action,
      by,
      surface,
    });
    if (action === 'end') {
      // The same closing-message dialog the stage bar opens: nothing ends
      // until the employer confirms there.
      setAssistantOpen(false);
      openEnd('assistant');
      return;
    }
    void changeStage(action);
  };

  const pickAssistantDraft = (reply: string, index: number, by: 'press' | 'shortcut' = 'press') => {
    assistant.select('reply', String(index));
    trackAssistantTaken({
      draftIndex: index,
      recommended: isRecommendedDraft(assistant.recommended, index, assistant.replies.length),
      by,
      surface,
    });
    composerRef.current?.insert(reply);
  };

  /** The unassisted card's drafts: openers, with no reading to mark them against. */
  const pickQuickReply = (reply: string) => {
    emitToHost('quick-reply-click', { message: reply, source: quickReplies.source });
    // `reply_source`, not `source`: the BFF stamps a top-level `source` the
    // backend copies over any property of the same name, erasing ai-vs-static.
    track('quick_reply_used', { reply_source: quickReplies.source, surface });
    // Records WHICH opener was used against the upstream audit row; a no-op for
    // the static set.
    quickReplies.select(reply);
    composerRef.current?.insert(reply);
  };

  // One report per reading that actually reached the employer, keyed on the
  // channel the reading was DRAWN from — see the same guard on /chat.
  const shownKey =
    card === 'assistant' && !assistant.reading && assistant.readingFor === channel.id
      ? `${assistant.readingFor}:${assistant.intro}`
      : null;
  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shownKey || shownRef.current === shownKey) return;
    shownRef.current = shownKey;
    trackAssistantShown({ suggest: assistant.suggest, status, surface });
  }, [shownKey, assistant.suggest, status]);

  const toggleAssistant = (open: boolean) => {
    const { reading, suggest } = assistant;
    trackAssistantToggled({ open, reading, suggest, status, surface });
    setAssistantOpen(open);
  };

  /* `MessageThread` re-pins on a footer arriving and on the scroll box resizing,
     and opening the card is neither: its content grew, the box did not. Without
     this an opened card runs past the foot with nothing scrolling to it. */
  useEffect(() => {
    if (assistantOpen) assistantRef.current?.scrollIntoView({ block: 'nearest' });
  }, [assistantOpen]);

  /** Open the closing-message dialog, reported as /chat's ending is. */
  const openEnd = (from: HiringSurface) => {
    const variant = closingVariantFor(jobBounded);
    const { channelType, channelId } = cidParts(channel.id);
    track('end_chat_pill_clicked', {
      channel_id: channelId,
      variant,
      surface: from,
      via: 'button',
    });
    emitToHost('end-chat-pill-click', { channelId, channelType, variant });
    track('end_chat_draft_fetched', { channel_id: channelId, fallback: true });
    setEnding({ variant, surface: from });
  };

  /** Back to the list once it has ended; a failure stays here and says so. */
  const confirmEnd = async (text: string) => {
    if (!ending) return;
    const { variant } = ending;
    setEnding(null);
    setWriteError(null);
    try {
      await convo.endWithMessage(text, variant);
      onSent();
      (onBack ?? onClose)();
    } catch {
      setWriteError(
        variant === 'reject' ? 'Could not reject the applicant.' : 'Could not end the chat.',
      );
    }
  };

  /** A stage press from the bar, reported as /chat's are. */
  const markStage = (stage: HiringStage) => {
    trackHiringStage({
      stage,
      surface,
      via: 'button',
      channelId: cidParts(channel.id).channelId,
    });
    void changeStage(stage);
  };

  return (
    <>
      {/* /chat's header in its narrow form. Marked as the drag handle the old
          widget bar was; `useDrag` skips its buttons, so the name still opens
          Details on /chat. */}
      <div className={styles.head} data-drag-handle>
        <ConversationHeader
          counterpart={{ ...counterpart, profile: details.profile ?? counterpart.profile }}
          jobTitle={channel.jobMeta?.jobTitle}
          byEmail={channel.byEmail}
          appliedAt={channel.jobMeta && !pane ? details.appliedAt : undefined}
          stage={jobBounded && !terminal && stageDrawable ? status : undefined}
          actions={
            <>
              <DeletionCountdown
                channel={channel}
                counterpartName={counterpart.name}
                variant={closingVariantFor(jobBounded)}
                onEnd={() => openEnd('countdown')}
                onOpenChange={setCountdownOpen}
              />
              {terminal && jobBounded && <HiringStatusPill status={status} compact />}
              <button
                type="button"
                className={styles.report}
                onClick={() => setReport({})}
                title="Report jobseeker"
                aria-label="Report jobseeker"
              >
                <Icon name="flag" size={15} />
              </button>
              {/* Mobile only: the FAB is the close everywhere else. */}
              <button className={headerStyles.close} onClick={onClose} aria-label="Close chat">
                <Icon name="close" size={17} />
              </button>
            </>
          }
          onBack={onBack}
          compact
          detailsOpen={false}
          onToggleDetails={() => {
            track('profile_opened', { surface, channel_id: cidParts(channel.id).channelId });
            if (onViewProfile) onViewProfile();
            else onExpand('details');
          }}
          profilePending={details.loading || details.failed}
        />
      </div>
      {!terminal && stageDrawable && (
        <StageBar
          status={status}
          busy={saving || statusUnknown}
          onStage={markStage}
          onReject={() => openEnd(surface)}
          jobBounded={jobBounded}
          compact
        />
      )}
      {writeError && (
        <p className={styles.writeError} role="alert">
          {writeError} Please try again.
        </p>
      )}
      <MessageThread
        messages={messages}
        loading={loading}
        isOwnMessage={isOwnMessage}
        currentUserId={currentUserId}
        sendReply={(text, id, plain) => send(text, id, undefined, plain).then(() => undefined)}
        counterpartName={counterpart.name}
        onReplyTo={terminal ? undefined : draft.startReply}
        onEdit={terminal ? undefined : draft.startEdit}
        bounce={convo.bounce && { ...convo.bounce, surface, dismiss: understood }}
        onReport={(message) => setReport({ message })}
        className={styles.messages!}
        footer={
          /*
           * THE PAGE'S CARD IN ITS COMPACT SHAPE, not a second implementation of
           * it: one component owns what the assistant looks like and what its
           * keys do, so the two surfaces cannot drift.
           *
           * `legacy` here means the lane is on and this reading had nothing to
           * show. It keeps the name-and-arrow the widget has always drawn in
           * that case, which is today's behaviour exactly; what the widget
           * *should* draw there is AI-3092.
           */
          card === 'assistant' ? (
            <div ref={assistantRef} className={styles.assistant}>
              <AssistantCard
                expanded={assistantOpen}
                onExpandedChange={toggleAssistant}
                reading={assistant.reading}
                suggest={assistant.reading ? null : assistant.suggest}
                flagged={assistant.flagged}
                disabled={saving}
                compact={{
                  jobBounded,
                  status,
                  replies: assistant.replies,
                  recommended: assistant.recommended,
                  translation: trackingTranslation(
                    assistant.translation,
                    assistant.recommended,
                    surface,
                  ),
                  onTake: takeAssistantAction,
                  onPick: pickAssistantDraft,
                  onWriteOwn: () => composerRef.current?.focus(),
                  // No intent: the plain expand asks for the chat, and the card
                  // is open at the foot of the thread on arrival.
                  onOpenPage: () => onExpand(),
                }}
              />
            </div>
          ) : card === 'legacy' ? (
            /*
             * THE SAME CARD, UNASSISTED. `assisted={false}` is what renames it
             * "Quick Actions" and drops the bot mark and the Beta badge — the
             * page's rule, carried over: nothing wears the assistant's name
             * without a reading behind it (AI-3092).
             */
            <div ref={assistantRef} className={styles.assistant}>
              <AssistantCard
                expanded={assistantOpen}
                onExpandedChange={setAssistantOpen}
                assisted={false}
                disabled={saving}
                compact={{
                  jobBounded,
                  status,
                  replies: quickReplies.replies,
                  // Static openers, not a reading — nothing to mark, on a row
                  // or on a draft.
                  recommended: null,
                  onTake: takeAssistantAction,
                  onPick: pickQuickReply,
                  onWriteOwn: () => composerRef.current?.focus(),
                  onOpenPage: () => onExpand(),
                }}
              />
            </div>
          ) : undefined
        }
      />
      {/* `terminal` should be near-unreachable — the list is inbox-only, and an
          ended chat isn't in it. Kept because the chat can end from another tab
          while this panel sits open (and a reject leaves the channel open for a
          while), and a composer that silently no-ops is worse than one that says
          why. */}
      <ConversationComposer
        ref={composerRef}
        convo={convo}
        formLibrary={formLibrary}
        conversationId={channel.id}
        contained
        onSend={submit}
        onUpload={uploadAttachment}
        voice={{ surface, channelId: cidParts(channel.id).channelId }}
        quoted={draft.staged}
        onCancelQuote={draft.cancel}
        notice={draft.notice}
        disabled={sending || terminal}
        placeholder={terminal ? 'This chat has ended' : 'Reply…'}
        rewardCoins={reward.eligible && !terminal ? reward.coins : undefined}
        // Half of a 400px panel is a reply floating over no conversation, and a
        // message long enough to need that room is what the full page is for.
        noExpand
        onTemplates={() => {
          emitToHost('template-click', {});
          setTemplatesOpen(true);
        }}
      />
      {/* Same panel, same hook, same events as /chat — a template the employer
          saved on the page is the same template here, and a host counting
          template use gets one story from both surfaces. */}
      {report && (
        <ReportDialog
          key={channel.id}
          cid={channel.id}
          message={report.message}
          counterpartName={counterpart.name}
          onClose={() => setReport(null)}
        />
      )}
      {ending && (
        <EndChatDialog
          key={channel.id}
          variant={ending.variant}
          counterpartName={counterpart.name}
          onCancel={() => {
            track('end_chat_draft_cancelled', { channel_id: cidParts(channel.id).channelId });
            setEnding(null);
          }}
          onConfirm={(text) => void confirmEnd(text)}
        />
      )}
      {templatesOpen && (
        <TemplatePanel
          templates={templates.templates}
          loading={templates.loading}
          error={templates.error}
          canCreateMore={templates.canCreateMore}
          onFetch={templates.fetch}
          context={{ candidateName: counterpart.name, jobTitle: channel.jobMeta?.jobTitle }}
          onSendJob={convo.sendJob}
          onUse={(t, text) => {
            emitToHost('template-use', { id: t.id, name: t.name, template: t.template });
            track('template_used', { template_id: t.id });
            composerRef.current?.insert(text);
          }}
          onCreate={templates.create}
          onUpdate={templates.update}
          onDelete={templates.remove}
          onClose={() => setTemplatesOpen(false)}
        />
      )}
    </>
  );
}
